import { z } from 'zod';
import { zStringRequired } from '../../z';
/** Card titles for the v2 discharge summary. */
export const dischargeSummaryV2Sections = {
    deadlines: 'Discharge Deadlines',
    transfer: 'Transfer',
    reason: 'Reason for Discharge',
    goals: 'Goal Outcomes',
    clinical: 'Clinical Summary',
    measures: 'Measures — first and last values this episode',
    labs: 'Laboratory data',
    handoff: 'Handoff',
    instructions: 'Discharge Instructions',
    notices: 'Notices (NOMNC / HHCCN)',
    sentToPcp: 'Sent to PCP',
};
export const physicianAgreementTitles = {
    goalsMet: 'Goals in the plan of care were met',
    noLongerNeedsServices: 'Patient no longer needs agency services',
};
/** Destinations that name a receiving provider and record that the medication list was provided. */
const receivingProviderDestinations = ['pcp', 'hospice', 'another-hha', 'snf', 'irf', 'ltch'];
export const needsReceivingProvider = (destination) => receivingProviderDestinations.includes(destination ?? '');
/** PCP is a physician. The other receiving destinations are a facility or agency, named in free text. */
export const receivingProviderIsPerson = (destination) => destination === 'pcp';
export const oasisInpatientFacilityTitle = 'Inpatient facility';
export const oasisDischargeDispositionTitle = 'Discharge disposition';
// Fields are nullish so a partial discharge always saves. Completeness checks compare what was entered
// to the discharge context loaded for the form (live, or as of its submitted date). They are not stored.
export const dischargeReasonOptions = [
    { value: 'payer-will-not-pay', label: 'Payer will not pay for services' },
    { value: 'goals-achieved', label: 'Goals in the plan of care were achieved' },
    { value: 'patient-refuses', label: 'Patient refuses services or chooses to leave' },
    { value: 'for-cause', label: 'For cause — disruptive, abusive, or uncooperative behaviour' },
    { value: 'died', label: 'Patient died' },
    { value: 'agency-closing', label: 'Agency is closing' },
    { value: 'patient-moved', label: 'Patient moved' },
    { value: 'md-order', label: 'MD order' },
    { value: 'no-skilled-service', label: 'No skilled service' },
    { value: 'hospitalized', label: 'Hospitalized' },
    { value: 'admitted-to-snf', label: 'Admitted to SNF' },
    { value: 'admitted-to-icf', label: 'Admitted to ICF' },
    { value: 'other', label: 'Other' },
];
export const goalOutcomeOptions = [
    { value: 'met', label: 'Met' },
    { value: 'progressing', label: 'Progressing' },
    { value: 'not-met', label: 'Not met' },
    { value: 'not-applicable', label: 'Not applicable' },
];
/** Labels for disciplines that appear on the care plan. */
export const dischargeGoalDisciplines = [
    { value: 'SN', label: 'Skilled Nursing' },
    { value: 'PT', label: 'Physical Therapy' },
    { value: 'OT', label: 'Occupational Therapy' },
    { value: 'ST', label: 'Speech Therapy' },
    { value: 'MSW', label: 'Medical Social Worker' },
    { value: 'HHA', label: 'Home Health Aide' },
];
export const dischargeMeasureOptions = [
    { value: 'bloodPressure', label: 'Blood pressure', unit: 'mmHg' },
    { value: 'heartRate', label: 'Heart rate', unit: 'bpm' },
    { value: 'respiratoryRate', label: 'Respiratory rate', unit: '/min' },
    { value: 'temperature', label: 'Temperature', unit: '°F' },
    { value: 'weight', label: 'Weight', unit: 'lbs' },
    { value: 'bloodSugar', label: 'Blood sugar', unit: 'mg/dL' },
    { value: 'pain', label: 'Pain', unit: '/10' },
];
export const dischargeDestinationOptions = [
    { value: 'home', label: 'Home or somewhere not listed below' },
    { value: 'another-hha', label: 'Another home health agency' },
    { value: 'snf', label: 'Skilled nursing facility (SNF)' },
    { value: 'irf', label: 'Inpatient rehabilitation facility (IRF)' },
    { value: 'ltch', label: 'Long-term care hospital (LTCH)' },
    { value: 'pcp', label: 'PCP' },
    { value: 'hospice', label: 'Hospice' },
];
export const deliveryMethodOptions = [
    { value: 'in-person', label: 'In person' },
    { value: 'fax', label: 'Fax' },
    { value: 'secure-email', label: 'Secure email' },
    { value: 'ehr', label: 'EHR exchange' },
    { value: 'mail', label: 'Mail' },
];
export const nomncUnplannedReasonOptions = [
    { value: 'hospitalized', label: 'Patient was hospitalized before notice could be given' },
    { value: 'died', label: 'Patient died' },
    { value: 'unreachable', label: 'Patient could not be reached' },
    { value: 'left-without-notice', label: 'Patient left the service area without notice' },
];
// Labels for the OASIS answers the discharge summary shows read-only.
export const oasisMedListProvidedLabels = {
    '0': 'No — not provided',
    '1': 'Yes — provided',
    '2': 'NA — the agency was not made aware of the transfer in time',
};
export const oasisMedListRouteLabels = {
    A: 'Electronic health record',
    B: 'Health information exchange',
    C: 'Verbal',
    D: 'Paper-based',
    E: 'Other',
};
/** "Yes — provided via Electronic health record, Paper-based", straight from the OASIS. Undefined when not answered. */
export function oasisMedListAnswer(provided, routes) {
    if (!provided) {
        return undefined;
    }
    const label = oasisMedListProvidedLabels[provided] ?? provided;
    const via = (routes ?? []).map((route) => oasisMedListRouteLabels[route] ?? route).join(', ');
    return via ? `${label} via ${via}` : label;
}
/** M2410 */
export const oasisInpatientFacilityLabels = {
    '01': 'Hospital',
    '02': 'Rehabilitation facility',
    '03': 'Nursing home',
    '04': 'Hospice',
    NA: 'No inpatient facility admission',
};
/** M2420 */
export const oasisDischargeDispositionLabels = {
    '01': 'Remained in the community (without formal assistive services)',
    '02': 'Remained in the community (with formal assistive services)',
    '03': 'Transferred to a non-institutional hospice',
    '04': 'Moved to a location not served by this agency',
    UK: 'Other unknown',
};
export const physicianAgreementSchema = z.object({
    /** Agency physician id. An empty value starts as the patient's primary physician. */
    agreedBy: z.string().nullish(),
    agreedAt: z.string().nullish(),
});
/** The summary's own yes/no. Whether the OASIS recorded it is read from the chart, not stored here. */
export const providerDeliverySchema = z.object({
    provided: z.boolean().nullish(),
});
export const patientDeliverySchema = z.object({
    recipient: z.string().nullish(),
    date: z.string().nullish(),
});
export const transferDetailsSchema = z.object({
    planned: z.boolean().nullish(),
    /** The unplanned transfer clock starts here. Never defaulted from the transfer date. */
    agencyAwareDate: z.string().nullish(),
    /** False means no transfer summary is owed. */
    stillInFacility: z.boolean().nullish(),
});
export const forCauseDetailsSchema = z.object({
    warnedAt: z.string().nullish(),
    resolutionAttemptedAt: z.string().nullish(),
    resolutionNotes: z.string().nullish(),
    otherProviderContactsGivenAt: z.string().nullish(),
    documentedInChartAt: z.string().nullish(),
});
export const dischargeReasonDetailsSchema = z
    .object({
    goalsAchieved: z
        .object({
        goalsMet: physicianAgreementSchema.nullish(),
        noLongerNeedsServices: physicianAgreementSchema.nullish(),
    })
        .nullish(),
    forCause: forCauseDetailsSchema.nullish(),
})
    .nullish();
export const goalOutcomeSchema = z.object({
    /** Care-plan goal id. Rows come from the care plan and are never added or removed here. */
    goalId: z.string(),
    goalTitle: z.string().nullish(),
    outcome: z.string().nullish(),
    remaining: z.string().nullish(),
});
/** First/last values come from the discharge context; a row only holds why a measure is missing. */
export const dischargeMeasureSchema = z.object({
    measure: z.string(),
    missingReason: z.string().nullish(),
});
export const handoffSchema = z.object({
    /** Agency physician id, when the receiving provider is the PCP. An empty value starts as the patient's primary physician. */
    receivingPhysicianId: z.string().nullish(),
    /** Name of the facility or agency, when the receiving provider is not a person. */
    receivingProviderName: z.string().nullish(),
    /** The physical therapy discharge summary was sent. */
    ptDischargeSummarySent: z.boolean().nullish(),
    /** The occupational therapy discharge summary was sent. */
    otDischargeSummarySent: z.boolean().nullish(),
    openIssues: z.string().nullish(),
    firstWeekActions: z.string().nullish(),
});
export const medicationListSchema = z.object({
    /** Yes/no on the handoff, when the destination names a receiving provider. */
    toNextProvider: providerDeliverySchema.nullish(),
    /** OASIS A2123. Hidden for reason for assessment 8 (died at home). */
    toPatientOrCaregiver: patientDeliverySchema.nullish(),
});
/** Delivery and signatures come from the NOMNC/HHCCN visit forms; only what those forms can't say lives here. */
export const noticesSchema = z.object({
    /** Why the NOMNC could not be given before an unplanned discharge. */
    unplannedReason: z.string().nullish(),
    unplannedReasonNote: z.string().nullish(),
    /** Services were reduced or ended for non-coverage reasons, so an HHCCN is owed. */
    hhccnRequired: z.boolean().nullish(),
});
export const sentToPcpSchema = z.object({
    /** Agency physician id. An empty value starts as the patient's primary physician. */
    recipientPhysicianId: z.string().nullish(),
    method: z.string().nullish(),
    sentDate: z.string().nullish(),
});
export const baseDischargeSummaryV2 = z
    .object({
    transfer: transferDetailsSchema.nullish(),
    dischargeReason: zStringRequired(),
    /** Required when `dischargeReason` is 'other'; cleared when another reason is picked. */
    dischargeReasonOther: z.string().nullish(),
    dischargeReasonDetails: dischargeReasonDetailsSchema,
    goalOutcomes: z.array(goalOutcomeSchema).nullish(),
    /** Explains a goals-achieved discharge that still has unmet goals. */
    goalsAchievedExplanation: z.string().nullish(),
    measures: z.array(dischargeMeasureSchema).nullish(),
    courseOfCare: z.string().nullish(),
    /** Not asked. Kept so a previously saved condition is not dropped on the next save. */
    conditionOfDischarge: z.string().nullish(),
    /** What the patient or caregiver was told to do after discharge. Not asked for reason for assessment 8 (died at home). */
    dischargeInstructions: z.string().nullish(),
    /** The patient or caregiver repeated the instructions back. */
    verbalizedUnderstanding: z.boolean().nullish(),
    /** Every discipline on the case was told about the discharge and its visits stopped. */
    allServicesNotified: z.boolean().nullish(),
    handoff: handoffSchema.nullish(),
    /** Not asked. Kept so a previously saved value is not dropped on the next save. */
    treatmentPreferences: z.string().nullish(),
    /** Not asked when the patient died at home (reason for assessment 08). */
    destination: z.string().nullish(),
    medicationList: medicationListSchema.nullish(),
    notices: noticesSchema.nullish(),
    sentToPcp: sentToPcpSchema.nullish(),
})
    .superRefine((data, ctx) => {
    if (data.dischargeReason === 'other' && !data.dischargeReasonOther?.trim()) {
        ctx.addIssue({ code: 'custom', path: ['dischargeReasonOther'], message: 'Required' });
    }
});
export const dischargeSummaryValidationSchemaV2 = z.object({
    id: z.string(),
    data: baseDischargeSummaryV2,
    // The form shell shows its standard StaffSignature block when the schema has these.
    signedUserId: zStringRequired(),
    signedUserDate: zStringRequired(),
});
